import { Box, Button, IconButton, Modal, Paper, Typography } from '@mui/material';
import { Close, PhotoCamera, Search } from '@mui/icons-material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { AccountData, EnrichedEventData, OrderData, OrderService, PropelUserModel } from '../../../api';
import { ApiError } from '../../../api/core/ApiError';
import { OrderStatus } from '../../../api/models/OrderStatus';
import { useAuth } from '../../../session/InternalAuthProvider';
import { getDateFormat, getTimeFormat } from '../../../common/getDateAndTimeFormat';
import CheckinDialog from '../CheckinDialog/CheckinDialog';
import ScannerView from './ScannerView';
import { ErrorOverlay, SuccessOverlay } from './ScanOverlay';
import SearchFallbackView from './SearchFallbackView';
import { decodeQrPayload } from './qrPayload';
import { playError, playSuccess, vibrate } from './feedback';
import { ErrorContext, useCheckinScannerMachine } from './useCheckinScannerMachine';
import { isExternalVoucherUnapproved } from '../../../externalVouchers/utils';

type CheckinScannerDialogProps = {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  event: EnrichedEventData;
  orders: OrderData[];
  updateOrder: (order: OrderData) => Promise<void>;
};

const CANCELLED_STATUSES: OrderStatus[] = [OrderStatus.ABANDONED, OrderStatus.ARCHIVED];

const CORNER_BRACKET_SIZE = 28;
const CORNER_BRACKET_THICKNESS = 3;
const CORNER_BRACKET_COLOR = 'common.white';

const cornerBase = {
  position: 'absolute' as const,
  width: CORNER_BRACKET_SIZE,
  height: CORNER_BRACKET_SIZE,
  borderColor: CORNER_BRACKET_COLOR,
  borderStyle: 'solid' as const,
  borderWidth: 0,
  pointerEvents: 'none' as const,
};

const CornerBrackets = () => (
  <>
    <Box
      sx={{
        ...cornerBase,
        top: 10,
        left: 10,
        borderTopWidth: CORNER_BRACKET_THICKNESS,
        borderLeftWidth: CORNER_BRACKET_THICKNESS,
        borderTopLeftRadius: 6,
      }}
    />
    <Box
      sx={{
        ...cornerBase,
        top: 10,
        right: 10,
        borderTopWidth: CORNER_BRACKET_THICKNESS,
        borderRightWidth: CORNER_BRACKET_THICKNESS,
        borderTopRightRadius: 6,
      }}
    />
    <Box
      sx={{
        ...cornerBase,
        bottom: 10,
        left: 10,
        borderBottomWidth: CORNER_BRACKET_THICKNESS,
        borderLeftWidth: CORNER_BRACKET_THICKNESS,
        borderBottomLeftRadius: 6,
      }}
    />
    <Box
      sx={{
        ...cornerBase,
        bottom: 10,
        right: 10,
        borderBottomWidth: CORNER_BRACKET_THICKNESS,
        borderRightWidth: CORNER_BRACKET_THICKNESS,
        borderBottomRightRadius: 6,
      }}
    />
  </>
);

const CheckinScannerDialog = ({ isOpen, setIsOpen, event, orders, updateOrder }: CheckinScannerDialogProps) => {
  const { t } = useTranslation();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData | null;
  const accountId = account?.id;
  const users: PropelUserModel[] = account?.users ?? [];

  const { state, dispatch } = useCheckinScannerMachine();
  const ordersRef = useRef(orders);
  ordersRef.current = orders;
  const updateOrderRef = useRef(updateOrder);
  updateOrderRef.current = updateOrder;
  const processingQrRef = useRef<string | null>(null);

  const [checkinDialogOrder, setCheckinDialogOrder] = useState<OrderData | undefined>(undefined);

  useEffect(() => {
    if (!isOpen) {
      dispatch({ type: 'RESET' });
      setCheckinDialogOrder(undefined);
    }
  }, [isOpen, dispatch]);

  useEffect(() => {
    if (state.phase.kind === 'success_overlay') {
      playSuccess();
      vibrate(30);
    } else if (state.phase.kind === 'error_overlay') {
      playError();
      vibrate(100);
    }
  }, [state.phase.kind]);

  const buildError = useCallback(
    (reason: ErrorContext['reason'], extra: Partial<ErrorContext> = {}): ErrorContext => ({
      reason,
      ...extra,
    }),
    [],
  );

  const runCheckin = useCallback(
    async (order: OrderData): Promise<{ ok: true; order: OrderData } | { ok: false; error: ErrorContext }> => {
      try {
        const result = await OrderService.checkinOrder({
          order_id: order.id,
          persons: order.persons,
        });
        return { ok: true, order: result };
      } catch (err) {
        if (err instanceof ApiError && err.status === 409) {
          return { ok: false, error: buildError('already_checked_in') };
        }
        const message = err instanceof ApiError ? err.body?.message : undefined;
        return { ok: false, error: buildError('network_error', { networkMessage: message }) };
      }
    },
    [buildError],
  );

  const resolveOrderForQr = useCallback(
    async (orderId: string): Promise<{ order: OrderData | null; error?: ErrorContext }> => {
      const inRoster = ordersRef.current.find((o) => o.id === orderId);
      if (inRoster) return { order: inRoster };
      try {
        const fetched = await OrderService.getOrder(orderId);
        if (fetched.account_id !== accountId) {
          return { order: null, error: buildError('wrong_account') };
        }
        if (fetched.parent_id !== event.id) {
          return { order: null, error: buildError('wrong_event') };
        }
        return { order: fetched };
      } catch (err) {
        if (err instanceof ApiError && (err.status === 404 || err.status === 403)) {
          return { order: null, error: buildError('invalid_qr') };
        }
        const message = err instanceof ApiError ? err.body?.message : undefined;
        return { order: null, error: buildError('network_error', { networkMessage: message }) };
      }
    },
    [accountId, event.id, buildError],
  );

  useEffect(() => {
    if (state.phase.kind !== 'processing') {
      processingQrRef.current = null;
      return undefined;
    }
    const { qr } = state.phase;
    if (processingQrRef.current === qr) return undefined;
    processingQrRef.current = qr;
    let cancelled = false;

    void (async () => {
      let payload;
      try {
        payload = decodeQrPayload(qr);
      } catch {
        if (!cancelled) dispatch({ type: 'CHECKIN_ERROR', error: buildError('invalid_qr') });
        return;
      }

      if (payload.a !== accountId) {
        if (!cancelled) dispatch({ type: 'CHECKIN_ERROR', error: buildError('wrong_account') });
        return;
      }

      const { order, error: resolveError } = await resolveOrderForQr(payload.o);
      if (cancelled) return;
      if (!order) {
        dispatch({ type: 'CHECKIN_ERROR', error: resolveError || buildError('order_not_in_roster') });
        return;
      }

      if (CANCELLED_STATUSES.includes(order.status)) {
        dispatch({ type: 'CHECKIN_ERROR', error: buildError('order_cancelled') });
        return;
      }

      if (isExternalVoucherUnapproved(order)) {
        dispatch({ type: 'CHECKIN_ERROR', error: buildError('external_voucher_not_approved') });
        return;
      }

      if (order.checkin) {
        dispatch({
          type: 'CHECKIN_ERROR',
          error: buildError('already_checked_in', {
            checkedInBy: order.checkin.checked_in_by,
            checkedInAt: order.checkin.created_at,
          }),
        });
        return;
      }

      const result = await runCheckin(order);
      if (cancelled) return;
      if (result.ok) {
        dispatch({ type: 'CHECKIN_SUCCESS', order: result.order });
        void updateOrderRef.current(result.order);
      } else {
        dispatch({ type: 'CHECKIN_ERROR', error: result.error });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [state.phase, accountId, dispatch, buildError, resolveOrderForQr, runCheckin]);

  const handleScan = useCallback(
    (qr: string) => {
      dispatch({ type: 'SCAN_RECEIVED', qr, at: Date.now() });
    },
    [dispatch],
  );

  const handlePermissionDenied = useCallback(() => {
    dispatch({ type: 'PERMISSION_DENIED' });
  }, [dispatch]);

  const handleDismissOverlay = useCallback(() => {
    dispatch({ type: 'OVERLAY_TIMEOUT' });
  }, [dispatch]);

  const handleRowTap = useCallback((order: OrderData) => {
    setCheckinDialogOrder(order);
  }, []);

  const inSearchMode = state.phase.kind === 'search_mode' || state.phase.kind === 'permission_denied';
  const cameraPaused =
    state.phase.kind === 'success_overlay' || state.phase.kind === 'error_overlay' || state.phase.kind === 'processing';

  return (
    <Modal
      open={isOpen}
      onClose={() => setIsOpen(false)}
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
      <Paper
        elevation={12}
        sx={{
          width: '100%',
          maxWidth: 380,
          maxHeight: 'calc(100vh - 32px)',
          borderRadius: '8px',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          outline: 'none',
        }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            px: 3,
            py: 2,
            borderBottom: 1,
            borderColor: 'divider',
          }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h4" noWrap>
              {t('checkin_scanner.title')}
            </Typography>
            {event.date && (
              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                {moment(event.date).format('ddd')}
                {' · '}
                {moment(event.date).format(getDateFormat(account || undefined).longDateFormat)}
                {event.time != null && (
                  <>
                    {' · '}
                    <span dir="ltr" style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {moment
                        .unix(event.time)
                        .utc()
                        .format(getTimeFormat(account || undefined))}
                    </span>
                  </>
                )}
              </Typography>
            )}
          </Box>
          <IconButton onClick={() => setIsOpen(false)} size="small" aria-label={t('close')}>
            <Close fontSize="small" />
          </IconButton>
        </Box>

        {inSearchMode ? (
          <SearchFallbackView
            orders={orders}
            account={account}
            permissionDenied={state.phase.kind === 'permission_denied'}
            onRowTap={handleRowTap}
          />
        ) : (
          <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                aspectRatio: '1 / 1',
                borderRadius: '8px',
                overflow: 'hidden',
                backgroundColor: 'mb.ink',
              }}>
              <ScannerView
                paused={cameraPaused}
                onScan={handleScan}
                onPermissionDenied={handlePermissionDenied}
                torchOn={state.torchOn}
                onTorchChange={(on) => dispatch({ type: 'SET_TORCH', on })}
              />
              <CornerBrackets />
              {state.phase.kind === 'success_overlay' && (
                <SuccessOverlay order={state.phase.order} onDismiss={handleDismissOverlay} />
              )}
              {state.phase.kind === 'error_overlay' && (
                <ErrorOverlay error={state.phase.error} account={account} users={users} />
              )}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              {t('checkin_scanner.aim_at_qr')}
            </Typography>
          </Box>
        )}

        <Box sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'center' }}>
          {inSearchMode ? (
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<PhotoCamera />}
              onClick={() => dispatch({ type: 'EXIT_SEARCH' })}
              disabled={state.phase.kind === 'permission_denied'}>
              {t('checkin_scanner.exit_search')}
            </Button>
          ) : (
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<Search />}
              onClick={() => dispatch({ type: 'ENTER_SEARCH' })}>
              {t('checkin_scanner.enter_search')}
            </Button>
          )}
        </Box>

        <CheckinDialog
          order={checkinDialogOrder}
          isOpen={!!checkinDialogOrder}
          setIsOpen={(open) => {
            if (!open) setCheckinDialogOrder(undefined);
          }}
          updateItem={updateOrder}
        />
      </Paper>
    </Modal>
  );
};

export default CheckinScannerDialog;
